/**
 * 진행표시(trace) 보간 args 전처리 (화면 무관 프리미티브)
 *
 * 값이 다시 i18n 키인 조건부 문구(예: fillStatus="위험…")를 "xxxKey" 규약으로 넘기면
 * 여기서 t()로 재해석해 {{xxx}} 자리에 번역값을 넣는다.
 *   { fillStatusKey: "traceRpFillStatusRisk", fillStatusKeyArgs: {count: 3} } → { fillStatus: t(키, args) }
 * 규약 밖 값은 그대로 통과. 백엔드가 순수 상태키를 넘겨 부분 한글 노출(반쪽 번역)을 방지.
 *
 * 목록형은 "xxxKeys"(i18n 키 배열) + "xxxMore"(생략 개수) 규약으로 한 문자열로 합친다.
 *   { dimsKeys: ["chatDimItem","chatDimCustomer"], dimsMore: 2 } → { dims: "품목·거래처 외 2개" }
 * "Keys".endsWith("Key") 는 false 라 위 단수 규약과 충돌하지 않는다.
 */
export function resolveTraceArgs(t, args) {
  if (!args || typeof args !== "object") return undefined;
  const out = {};
  const used = new Set();
  // 1패스 — 목록 규약. 백엔드가 상위 N개만 담고 나머지는 More 로 세므로, 사전에 없는 값
  //   (내부 식별자)이 화면으로 새지 않는다. xxxMore 를 여기서 소비해야 2패스에 안 남는다.
  for (const [k, v] of Object.entries(args)) {
    if (!k.endsWith("Keys") || !Array.isArray(v)) continue;
    const base = k.slice(0, -4);
    used.add(k);
    used.add(`${base}More`);
    const list = v.filter(x => typeof x === "string" && x).map(x => t(x))
                  .join(t("chatListSep"));
    // ⚠️ 보간 변수명에 count 를 쓰지 않는다 — i18next 가 복수형 접미(_other) 키를 찾아 미스가 난다.
    const n = Number(args[`${base}More`]) || 0;
    out[base] = n > 0 ? t("chatListMore", { list, n }) : list;
  }
  // 2패스 — 기존 단수 규약 + 나머지 통과
  for (const [k, v] of Object.entries(args)) {
    if (used.has(k) || k.endsWith("KeyArgs")) continue;   // KeyArgs 는 짝(Key)에서 함께 소비
    if (k.endsWith("Key") && typeof v === "string") {
      out[k.slice(0, -3)] = t(v, args[`${k}Args`] || undefined);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * 서버 병행 키 렌더 — 키가 실제로 번역되면 그 값, 아니면 넘겨받은 원문(ko) 폴백.
 *
 * 번역이 빠져도 키 문자열(내부 식별자 계열 ASCII)이 사용자에게 노출되지 않는다.
 * args 안의 `xxxKey` 는 resolveTraceArgs 규약대로 함께 풀린다.
 * (pages/ai/aiFieldText 의 tKeyOr 가 이 함수를 그대로 쓴다 — 정의는 여기 하나다.)
 */
export function keyedText(t, key, fallback, args) {
  if (!t || !key) return fallback ?? "";
  const out = t(key, { ...(resolveTraceArgs(t, args) || {}) });
  return (out && out !== key) ? out : (fallback ?? "");
}

/**
 * 번역표에 없는 키가 화면에 그대로 뜨는 것을 막는 최후 문구.
 *
 * i18n 키는 도구 함수명·내부 식별자와 같은 계열의 ASCII 표기라, 번역이 빠지면
 * i18next 기본 동작으로 키 문자열이 사용자에게 그대로 노출된다. ASCII 키일 때만
 * 범용 문구로 떨어뜨린다 — 구 호출부가 넘기는 한국어 리터럴 라벨은 원문을 유지한다.
 */
export function labelFallback(t, key) {
  return /^[A-Za-z][A-Za-z0-9]*$/.test(key || "") ? t("chatToolGeneric") : (key || "");
}

/** 진행표시 라벨 렌더 — 보간 args 해석 + 키 노출 차단. */
export function traceLabel(t, key, args) {
  if (!key) return "";
  return t(key, { ...(resolveTraceArgs(t, args) || {}), defaultValue: labelFallback(t, key) });
}

export default resolveTraceArgs;
